import Steps from "common/Steps/Steps";
import Modal from "common/Modal/Modal";
import { useState } from "react";
import { useTheme } from "styled-components";
import Button from "common/Buttons/Button";
import { affiliationService } from "app/services/affiliation.service";
import CreateOrEditStep from "features/affiliation/ReferralLinks/CreateLink/CreateOrEditStep";
import ShareStep from "features/affiliation/ReferralLinks/CreateLink/ShareStep";
import { CreateReferralLinkRequest } from "types/affiliation.types";
import { displayResultMessage } from "utils/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

import { LanguageNameSpaces } from "@shared/types";

interface CreateLinkModalProps {
  isVisible: boolean;
  closeModal: () => void;
}

const CreateLinkModal = ({ isVisible, closeModal }: CreateLinkModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const { isNewLook } = useTheme();
  const [step, setStep] = useState(ModalSteps.CREATE_STEP);
  const [referralCode, setReferralCode] = useState<string>();

  const [createReferralLink, { isLoading: createLoading }] =
    affiliationService.useCreateReferralLinkMutation();

  const closeCreateModal = () => {
    setStep(ModalSteps.CREATE_STEP);
    closeModal();
  };

  const createReferralLinkHandler = (values: CreateReferralLinkRequest) => {
    return displayResultMessage(
      createReferralLink({
        referral_code: values.referral_code,
        description: values.description || "",
      }).unwrap(),
      undefined,
      t("links.create_error"),
      undefined,
      () => {
        setReferralCode(values.referral_code);
        setStep(ModalSteps.SHARE_STEP);
      }
    );
  };

  const steps = [
    {
      title: t("links.create_step"),
      content: <CreateOrEditStep onSubmit={createReferralLinkHandler} />,
    },
    {
      title: t("links.share_step"),
      content: <ShareStep referralCode={referralCode} />,
    },
  ];

  const footer = [
    <Button key="cancel" onClick={closeCreateModal}>
      {step === ModalSteps.CREATE_STEP ? t("common.cancel") : t("common.close")}
    </Button>,
  ];
  if (step === ModalSteps.CREATE_STEP) {
    footer.push(
      <Button
        key="create"
        type="primary"
        htmlType="submit"
        form="createLinkForm"
        loading={createLoading}
      >
        {t("common.create")}
      </Button>
    );
  }

  const items = steps.map((step) => ({
    title: step.title,
  }));

  return (
    <Modal
      onCancel={closeCreateModal}
      open={isVisible}
      title={t("links.create_link")}
      footer={footer}
      destroyOnHidden={true}
      width={500}
    >
      <Steps
        current={step}
        items={items}
        style={{ marginBottom: isNewLook ? 20 : 24 }}
        // New look: a compact horizontal stepper on every width.
        size={isNewLook ? "small" : undefined}
        responsive={!isNewLook}
      />
      <div>{steps[step].content}</div>
    </Modal>
  );
};

export default CreateLinkModal;

enum ModalSteps {
  CREATE_STEP = 0,
  SHARE_STEP = 1,
}
